/**
 * DFY queue widgets — Phase 4.
 *
 * Two views:
 *   <AdminDfyQueue />     — super admin: see ALL orders, assign operators, deliver
 *   <OperatorDfyQueue />  — affiliate operator: see only orders assigned to them, deliver
 *   <OperatorOptInToggle /> — affiliate-only switch to opt in/out of DFY assignments
 */
import { useEffect, useState } from "react";
import { Briefcase, CheckCircle, Clock, UserPlus, Truck, ToggleLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { usePublicPricing, fmt$ as fmtLive } from "@/lib/usePublicPricing";

const fmt$ = (cents) => `$${((cents || 0) / 100).toFixed(0)}`;
const STATUS_TONE = {
  paid_unassigned: "bg-[#FDF1ED] text-[#C16E5A]",
  in_progress: "bg-[#FFF4D9] text-[#8B6914]",
  delivered: "bg-[#E8F0E9] text-[#4A5D4E]",
  refunded: "bg-[#F0F1EE] text-[#6B6A68]",
  pending_payment: "bg-[#F0F1EE] text-[#6B6A68]",
};

function StatusBadge({ status }) {
  return (
    <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded ${STATUS_TONE[status] || "bg-[#F0F1EE]"}`}>
      {status?.replace(/_/g, " ")}
    </span>
  );
}

function OrderRow({ order, onAssign, onDeliver, mode = "admin" }) {
  return (
    <div className="bg-white border hairline rounded-md p-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between" data-testid={`dfy-order-${order.id}`}>
      <div className="flex-1 min-w-[200px]">
        <div className="flex items-center gap-2 flex-wrap">
          <strong className="text-sm text-[#1C1B1A]">{order.buyer_name || order.buyer_email}</strong>
          <StatusBadge status={order.status} />
        </div>
        <div className="text-xs text-[#6B6A68] mt-0.5">
          {order.buyer_email} · {fmt$(order.amount_cents)} · op payout {fmt$(order.operator_payout_cents)}
        </div>
        {order.buyer_youtube_url && (
          <a href={order.buyer_youtube_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#4A5D4E] hover:underline truncate inline-block max-w-md">
            {order.buyer_youtube_url}
          </a>
        )}
        {order.buyer_notes && (
          <div className="text-[11px] text-[#6B6A68] italic mt-0.5">{order.buyer_notes}</div>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {mode === "admin" && order.status === "paid_unassigned" && (
          <button
            onClick={() => onAssign && onAssign(order)}
            data-testid={`dfy-assign-${order.id}`}
            className="px-3 py-1.5 rounded-md bg-[#1C1B1A] text-white text-xs hover:bg-[#3A4A3E] flex items-center gap-1"
          >
            <UserPlus size={12} weight="duotone" /> Assign
          </button>
        )}
        {(order.status === "in_progress") && (
          <button
            onClick={() => onDeliver && onDeliver(order)}
            data-testid={`dfy-deliver-${order.id}`}
            className="px-3 py-1.5 rounded-md bg-[#4A5D4E] text-white text-xs hover:bg-[#3A4A3E] flex items-center gap-1"
          >
            <Truck size={12} weight="duotone" /> Mark delivered
          </button>
        )}
      </div>
    </div>
  );
}

// ============== Super Admin: full queue + assignment ==============
export function AdminDfyQueue() {
  const [orders, setOrders] = useState([]);
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(null); // order being assigned

  const load = async () => {
    try {
      const [q, ops] = await Promise.all([
        api.get("/dfy/admin-queue"),
        api.get("/dfy/available-operators"),
      ]);
      setOrders(q.data.orders || []);
      setOperators(ops.data.operators || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load DFY queue.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const doAssign = async (orderId, operatorUserId) => {
    try {
      await api.post(`/dfy/${orderId}/assign`, { operator_user_id: operatorUserId });
      toast.success("Assigned.");
      setPickerOpen(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Assign failed."); }
  };

  const doDeliver = async (order) => {
    if (!window.confirm(`Mark order for ${order.buyer_email} as DELIVERED? This creates the buyer's account, hard-locks attribution to the operator, and records the $${(order.operator_payout_cents/100).toFixed(0)} payout. Cannot be undone.`)) return;
    try {
      await api.post(`/dfy/${order.id}/deliver`, {});
      toast.success("Delivered + buyer notified.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delivery failed."); }
  };

  if (loading) return <div className="text-[#6B6A68] text-sm py-4">Loading DFY queue…</div>;

  const grouped = {
    paid_unassigned: orders.filter(o => o.status === "paid_unassigned"),
    in_progress: orders.filter(o => o.status === "in_progress"),
    delivered: orders.filter(o => o.status === "delivered"),
  };

  return (
    <div data-testid="admin-dfy-queue">
      <h2 className="font-serif-display text-2xl mb-3 flex items-center gap-2">
        <Briefcase size={20} weight="duotone" /> DFY queue
      </h2>
      <p className="text-sm text-[#6B6A68] mb-4 max-w-3xl">
        Done-for-You orders across the platform. Assign opted-in Creator Pro members as operators, or self-assign and deliver yourself.
      </p>
      {(["paid_unassigned", "in_progress", "delivered"]).map((bucket) => (
        <section key={bucket} className="mb-5" data-testid={`dfy-bucket-${bucket}`}>
          <h3 className="text-xs uppercase tracking-[0.2em] text-[#6B6A68] mb-2 flex items-center gap-2">
            {bucket === "paid_unassigned" && <Clock size={12} weight="duotone" />}
            {bucket === "in_progress" && <Briefcase size={12} weight="duotone" />}
            {bucket === "delivered" && <CheckCircle size={12} weight="duotone" />}
            {bucket.replace(/_/g, " ")} ({grouped[bucket].length})
          </h3>
          {grouped[bucket].length === 0 ? (
            <div className="text-xs text-[#6B6A68] italic">None.</div>
          ) : (
            <div className="grid grid-cols-1 gap-2">
              {grouped[bucket].map(o => (
                <OrderRow key={o.id} order={o} mode="admin"
                  onAssign={(order) => setPickerOpen(order)}
                  onDeliver={doDeliver}
                />
              ))}
            </div>
          )}
        </section>
      ))}

      {pickerOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="dfy-assign-modal">
          <div className="modal-safe bg-white rounded-xl p-5 max-w-md w-full">
            <h3 className="font-serif-display text-lg mb-2">Assign operator</h3>
            <p className="text-xs text-[#6B6A68] mb-3">For: {pickerOpen.buyer_name || pickerOpen.buyer_email}</p>
            <div className="space-y-1 mb-3">
              <button
                onClick={() => doAssign(pickerOpen.id, JSON.parse(localStorage.getItem("user") || "{}")?.id)}
                className="w-full text-left px-3 py-2 rounded-md hover:bg-[#F0F1EE] border hairline text-sm"
                data-testid="dfy-assign-self"
              >
                <strong>Self-assign</strong> · keep the build in-house
              </button>
              {operators.map(op => (
                <button key={op.id} onClick={() => doAssign(pickerOpen.id, op.id)}
                  data-testid={`dfy-assign-op-${op.id}`}
                  className="w-full text-left px-3 py-2 rounded-md hover:bg-[#F0F1EE] border hairline text-sm">
                  {op.name} · <span className="text-xs text-[#6B6A68]">{op.email}</span>
                </button>
              ))}
              {operators.length === 0 && (
                <div className="text-xs text-[#6B6A68] italic px-2">No Creator Pro members have opted in to DFY operator role yet.</div>
              )}
            </div>
            <button onClick={() => setPickerOpen(null)} className="text-xs text-[#6B6A68] hover:text-[#1C1B1A]">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============== Operator: queue limited to assignments + deliver ==============
export function OperatorDfyQueue() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get("/dfy/my-queue");
      setOrders(r.data.orders || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load your DFY queue.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const doDeliver = async (order) => {
    if (!window.confirm(`Mark this order DELIVERED? This locks attribution to you and credits your $${(order.operator_payout_cents/100).toFixed(0)} payout. Cannot be undone.`)) return;
    try {
      await api.post(`/dfy/${order.id}/deliver`, {});
      toast.success("Delivered + payout recorded.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delivery failed."); }
  };

  if (loading) return <div className="text-[#6B6A68] text-sm py-4">Loading…</div>;
  if (orders.length === 0) return null;

  return (
    <div className="bg-white border hairline rounded-xl p-5" data-testid="operator-dfy-queue">
      <h3 className="font-serif-display text-lg mb-3 flex items-center gap-2">
        <Briefcase size={18} weight="duotone" /> Your DFY assignments
      </h3>
      <div className="grid grid-cols-1 gap-2">
        {orders.map(o => (
          <OrderRow key={o.id} order={o} mode="operator" onDeliver={doDeliver} />
        ))}
      </div>
    </div>
  );
}

// ============== Operator opt-in toggle ==============
export function OperatorOptInToggle({ initialEnabled = false }) {
  const [enabled, setEnabled] = useState(initialEnabled);
  const [saving, setSaving] = useState(false);
  // Oct 3 2026 — this card hardcoded "$700 each", which is the OPERATOR's
  // share, not a price anyone set: it is dfy_price_cents × operator_share_pct.
  // Both move on PricingLab sliders, so it is derived now and says nothing
  // numeric until both values have arrived.
  const pricing = usePublicPricing();
  const operatorCut = (pricing.dfy_price_cents != null && pricing.dfy_operator_share_pct != null)
    ? fmtLive(Math.round(pricing.dfy_price_cents * pricing.dfy_operator_share_pct / 100))
    : null;

  const flip = async () => {
    setSaving(true);
    try {
      const r = await api.post("/dfy/operator-toggle", { enabled: !enabled });
      setEnabled(r.data.dfy_operator_enabled);
      toast.success(r.data.dfy_operator_enabled
        ? "You'll start receiving DFY assignment emails."
        : "DFY assignment emails turned off.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update."); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white border hairline rounded-md p-3 flex items-center justify-between gap-3" data-testid="dfy-operator-toggle">
      <div className="flex-1 min-w-[200px]">
        <div className="text-sm text-[#1C1B1A] flex items-center gap-2">
          <ToggleLeft size={14} weight="duotone" /> Receive DFY operator assignments
        </div>
        <p className="text-xs text-[#6B6A68] mt-0.5">
          When ON, super admin can assign you Done-for-You orders
          {operatorCut ? ` (${operatorCut} each on delivery)` : " (your share is paid on delivery)"}.
          When OFF, you won't be emailed.
        </p>
      </div>
      <button
        onClick={flip}
        disabled={saving}
        data-testid="dfy-operator-toggle-btn"
        className={`px-3 py-1.5 rounded-md text-xs font-medium disabled:opacity-60 ${
          enabled ? "bg-[#4A5D4E] text-white hover:bg-[#3A4A3E]" : "bg-white border hairline text-[#6B6A68] hover:text-[#1C1B1A]"
        }`}
      >
        {saving ? "…" : (enabled ? "ON" : "OFF")}
      </button>
    </div>
  );
}
